<script setup lang="ts">
import { FormServices } from "@/service/FormServices";
import { SurveyServices } from "@/service/SurveyServices";
import { useAppStore } from "@/store/app";
import { PRODUCT_TYPE } from "@/util/constants";
import { UISpin } from "@gohighlevel/ghl-ui";
import { onMounted, ref } from "vue";
import { useRoute } from "vue-router";

const error = ref(false);
const loading = ref(true);
const store = useAppStore();
const route = useRoute();

const productType = ref(
  route.path.includes("form-builder")
    ? PRODUCT_TYPE.FORM
    : route.path.includes("survey-builder")
    ? PRODUCT_TYPE.SURVEY
    : route.path.includes("quiz-builder")
    ? PRODUCT_TYPE.QUIZ
    : PRODUCT_TYPE.FORM
);

/**
 * Creates a new asset (form, survey, or quiz) based on the current product type
 */
const createAsset = async (): Promise<void> => {
  await new Promise((resolve) => setTimeout(resolve, 2000));

  try {
    let response;
    let assetId;

    if (productType.value === PRODUCT_TYPE.FORM) {
      response = await FormServices.createForm({
        locationId: store.locationId,
      });
      assetId = response.data.form._id;
    } else if (productType.value === PRODUCT_TYPE.SURVEY) {
      response = await SurveyServices.createSurvey({
        locationId: store.locationId,
      });
      assetId = response.data.survey._id;
    } else {
      // Quiz creation
      response = await FormServices.createForm({
        locationId: store.locationId,
        productType: "quiz",
      });
      assetId = response.data.form._id;
    }

    // Wait for 0.5 seconds to ensure everything is processed
    await new Promise((resolve) => setTimeout(resolve, 500));

    if (store.appHandshake) {
      try {
        store.appHandshake
          .then((parent) => {
            const eventName =
              productType.value === PRODUCT_TYPE.FORM
                ? "formCreated"
                : productType.value === PRODUCT_TYPE.SURVEY
                ? "surveyCreated"
                : "quizCreated";

            parent.emit(eventName, {
              [productType.value === PRODUCT_TYPE.FORM
                ? "formId"
                : productType.value === PRODUCT_TYPE.SURVEY
                ? "surveyId"
                : "quizId"]: assetId,
              locationId: store.locationId,
            });
          })
          .catch((err) => {
            error.value = true;
            loading.value = false;
          });
      } catch (err) {
        error.value = true;
        loading.value = false;
      }
    }
  } catch (err) {
    error.value = true;
    loading.value = false;
  }
};

onMounted(() => {
  createAsset();
});
</script>

<template>
  <div class="flex h-screen flex-col items-center justify-center">
    <template v-if="loading && !error">
      <div class="mb-4">
        <UISpin size="large" />
      </div>
      <div class="text-sm">
        {{
          $t(
            productType === PRODUCT_TYPE.FORM
              ? "common.creatingForm"
              : productType === PRODUCT_TYPE.SURVEY
              ? "common.creatingSurvey"
              : "common.creatingQuiz"
          )
        }}
      </div>
    </template>
    <template v-else-if="error">
      <div class="font-bold text-red-600">
        {{
          $t(
            productType === PRODUCT_TYPE.FORM
              ? "common.errorCreatingForm"
              : productType === PRODUCT_TYPE.SURVEY
              ? "common.errorCreatingSurvey"
              : "common.errorCreatingQuiz"
          )
        }}
      </div>
    </template>
  </div>
</template>
